/**
 * @file NotificationManager.js
 * @project Web-panel
 * @author Pavel Shabardin (<bigbn@mail.ru>) Friday, 10th January 2020 10:11:20 am
 * @copyright 2015 - 2020 SKAT LLC, Delive LLC
 * @flow
 */

import type { iLogger, iNotificationManager, iEventEmitter, NotificationMessage, iGlobalEventBus, iCRUD, UserPreferences } from '../../types'
import type { ID } from 'web-panel-essentials/types'

import * as React from 'react'
import { Inject, Injectable } from '../../serviceLocator'
import mList from '../../utils/mList'
import { mix } from 'mixwith'
import cn from 'classnames'
import Delegate from '../../utils/Delegate'
import { NOTIFICATION, __, genTestName, isMobileMode } from 'web-panel/globals'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import isNil from 'lodash/isNil'
import autoBind from 'react-autobind'

const MIDDLE_MOUSE_BUTTON = 1
const NOTIFICATIONS_MODE_PREF_NAME = 'notifications-concealed'

class Icon extends React.PureComponent<{iconName: string}> {
  render () {
    const { iconName } = this.props
    if (!iconName) return null
    return (
      <div className='icon-container'>
        <FontAwesomeIcon className='notification-icon' icon={iconName} />
      </div>
    )
  }
}

class Notification extends React.PureComponent<Object> {
  handleMouseDown = (event: {button: number}) => {
    const { onClose, unclosable } = this.props
    if (event.button === MIDDLE_MOUSE_BUTTON && !unclosable) return onClose()
  }

  render () : React.Node {
    let { testName, message, details, type, progress, onClick, onClose, onDeny, wasAlreadyInvokedBefore, buttons = [], unclosable, icon } = this.props
    progress = Math.min(100, progress)

    return (
      <div {...genTestName(testName)} onClick={onClick} onMouseDown={this.handleMouseDown} className={cn('alert', ['alert', type].join('-'), 'alert-dismissable')}>
        <Icon iconName={icon} />
        <div className='message'>
          <span className={cn({ bold: Boolean(details) })}>{message}</span>
          {details ? <div className='details'>{details}</div> : null}
          <div className='buttons'>
            {buttons.map(({ title, action, type }, index) => <div key={index} onClick={action} type='button' className={cn('button', 'action-button', type ? ['button', type].join('-') : null)}><span>{title}</span></div>)}
          </div>
          {progress
            ? (
              <div className='progress'>
                <div style={{ width: [Number(progress), '%'].join('') }} className='inner-line' />
              </div>
            )
            : null}
          {
            wasAlreadyInvokedBefore && !unclosable ? (
              <div className='dont-show-again' onClick={onDeny}>
                {__('DONT_SHOW_SUCH_MESSAGES_ANYMORE')}
              </div>
            ) : null
          }
        </div>
        {unclosable ? null : <button onClick={onClose} type='button' className='close'>&times;</button>}
        <br className='clear' />
      </div>
    )
  }
}

/**
 * @class Notifications
 * @implements iNotificationManager
 */
class Notifications extends mix(React.Component).with(mList) implements iNotificationManager {
  @Inject logger : iLogger
  @Inject globalEventBus : iGlobalEventBus & iEventEmitter
  @Inject preferencesManager : iCRUD<UserPreferences>

  timers: Object
  lastNotifications: Set<ID>
  deniedNotifications: Set<ID>

  constructor (props: Object) {
    super(props)
    autoBind(this)

    this.logger.info('Initializing notification manager')
    this.timers = {}
    this.state = {
      compact: false,
      messages: this.defineList('messages')
    }
    this.lastNotifications = new Set()
    this.deniedNotifications = new Set()
    this.globalEventBus.registerEvent('toggle-notifications-mode')
    this.globalEventBus.on('notifications-mode-changed', this.toggleMode.bind(this))
  }

  componentDidMount () {
    this.toggleMode()
  }

  /**
   * @lends NotificationManager
   */
  async show ({ id, message, type = NOTIFICATION.DEFAULT, persistent = false, unclosable = false, timeout=5000, details, progress, buttons, testName, icon }: NotificationMessage) : Promise<ID> {
    id = id || Symbol('id')
    if (this.deniedNotifications.has(id)) return id

    if (isMobileMode()) unclosable = false // Иначе оно очень мешает нормально работать
    if (this.state.messages.find(message => message.id === id)) {
      await this.updateMessages([{ id, icon, message, details, progress }])
    } else {
      await this.addMessages([{
        id,
        icon,
        testName,
        message,
        details,
        type,
        buttons,
        progress,
        unclosable,
        wasAlreadyInvokedBefore: this.lastNotifications.has(id),
        onDeny: () => {
          this.deniedNotifications.add(id)
          this.removeMessages([{ id }])
        },
        onClose: () => this.removeMessages([{ id }])
      }])
    }

    this.lastNotifications.add(id)
    if (!persistent) {
      if (this.timers[id]) clearTimeout(this.timers[id])
      this.timers[id] = setTimeout(() => {
        this.removeMessages([{ id }])
        this.timers[id] = null
      }, timeout)
    }
    return id
  }

  /**
   * @lends NotificationManager
   */
  async update (id: mixed, params: NotificationMessage) {
    await this.updateMessages([{ id, ...params }])
  }

  /**
   * @lends NotificationManager
   */
  async hide (id: any) {
    clearTimeout(this.timers[id])
    this.timers[id] = null
    await this.removeMessages([{ id }])
  }

  /**
   * @lends NotificationManager
   */
  async toggleMode () {
    const newMode = await this.preferencesManager.read(NOTIFICATIONS_MODE_PREF_NAME)
    if (newMode && !isNil(newMode.value)) this.setState({ compact: !newMode.value })
  }

  render () : React.Node {
    const { messages } = this.state
    const className = cn('all-notifications', { compact: this.state.compact })
    return (
      <div className={className}>
        {messages.map((message, index) => <Notification key={index} {...message} />)}
      </div>
    )
  }
}

/**
 * Coo
 * @alias injectable:notificationManager
 * @class NotificationManager
 * @implements iNotificationManager
 */
@Injectable('notificationManager', true)
class NotificationManager extends Delegate {
  @Inject layout: any

  constructor () {
    super()
    const component = this.layout.renderView(<Notifications />, 'flashes-region')
    this.becomeProxyOf(component)
  }
}

export default NotificationManager
